import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Chip } from 'primereact/chip';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateForApi, formatTimeForApi } from '../../../../utils/dateUtils';
import { useGetLmsTrainingCatalogDropdownQuery, useGetLmsTrainingCatalogDetailByIdQuery, useGetUserGroupsQuery, useGetLmsNewsByPlanningIdQuery } from '../../../../api';
import LmsCodeDropdown from '../../components/LmsCodeDropdown';
import useLmsLanguageOptions from '../../hooks/useLmsLanguageOptions';
import InstructorUserDropdown from '../../components/InstructorUserDropdown';
import PlanningInstructorsTab from './PlanningInstructorsTab';
import PlanningNewsSection, { EMPTY_PLANNING_NEWS, toPlanningNewsForm } from '../../lms-news/components/PlanningNewsSection';

// Backend `currency` alanını doğrulamaz (serbest metin, büyük harfe çevrilerek saklanır — doc §8.1),
// ama diğer eğitim sayfalarındaki alanlar gibi burada da sabit bir dropdown kullanılıyor
// (bkz. src/pages/accounting/collateralReceived/hooks/useCollateralReceivedData.js — aynı desen).
const CURRENCY_OPTIONS = [
    { value: 'TRY', label: 'TRY' },
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' }
];
 
const PlanningForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, readOnly = false, instructorsReadOnly = false }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const toast = useRef(null);
    const isEdit = !!(initialData && initialData.id);

    const [formData, setFormData] = useState({
        id: null,
        trainingId: null,
        start: null,
        end: null,
        planningLocation: null,
        trainingCapacity: null,
        trainingLanguage: '',
        trainingDay: '',
        trainingDuration: null,
        preExamRequired: 0,
        preExamStart: null,
        preExamEnd: null,
        fee: null,
        currency: '',
        isActive: 1
    });
    const [localSubmitted, setLocalSubmitted] = useState(false);
    // Yalnızca ekleme modunda: kaydetmeden önce değiştirilebilen yerel eğitmen önerisi listesi.
    const [pendingInstructors, setPendingInstructors] = useState([]);
    const [pendingInstructorUserId, setPendingInstructorUserId] = useState(null);
    const [newsForm, setNewsForm] = useState(EMPTY_PLANNING_NEWS);
    const { data: existingNews, isFetching: isLoadingNews } = useGetLmsNewsByPlanningIdQuery(initialData?.id, { skip: !visible || !isEdit, refetchOnMountOrArgChange: true });

    const { data: catalogDropdownData, isFetching: isLoadingCatalogList } = useGetLmsTrainingCatalogDropdownQuery(undefined, { skip: isEdit });
    // Label kullanıcıya kod+ad birlikte göstersin diye "trainingCode - trainingName" - seçilince
    // gönderilen DEĞER hâlâ yalnızca `item.id` (bkz. optionValue="value" aşağıda), bu satır sadece
    // görüntüyü değiştirir. `GetDropdown` cevabının `trainingName` içerdiği gerçek network cevabıyla
    // doğrulandı (bu dosyanın başındaki kod yorumundaki "trainingName YOK" iddiası YANLIŞTI - bkz.
    // lmsTrainingCatalog.js'teki düzeltilmiş yorum).
    const catalogCodeOptions = useMemo(
        () => (catalogDropdownData || []).map((item) => ({ value: item.id, label: item.trainingName ? `${item.trainingCode} - ${item.trainingName}` : item.trainingCode })),
        [catalogDropdownData]
    );

    const { data: catalogDetail } = useGetLmsTrainingCatalogDetailByIdQuery(formData.trainingId, { skip: !formData.trainingId });
    const { data: userGroupsResponse } = useGetUserGroupsQuery(undefined, { skip: isEdit });

    // "Eğitim Dili" kullanıcı tarafından değiştirilebilir bir dropdown (Eğitim Kataloğu ekleme
    // formundakiyle aynı kaynak/desen — bkz. TrainingCatalogFormFields.jsx). trainingLanguage ve
    // trainingCapacity artık `LmsCatalogPlanning`'e de eklendiği için (BE güncellemesi, 2026-09-01)
    // handleSubmit'teki payload'a gönderiliyor.
    const { languageOptions, isLoadingLanguages } = useLmsLanguageOptions();

    const userNameById = useMemo(() => {
        const map = {};
        (userGroupsResponse?.data || []).forEach((user) => {
            map[user.id] = user.name;
        });
        return map;
    }, [userGroupsResponse]);

    const activeLanguageId = getCurrentLanguageId ? getCurrentLanguageId() : null;
    const contentOutputRow = useMemo(() => {
        const rows = catalogDetail?.contentOutput?.translations || [];
        if (!rows.length) return null;
        return rows.find((row) => row.languageId === activeLanguageId) || rows[0];
    }, [catalogDetail, activeLanguageId]);

    const combineDateTime = (datePart, timePart) => {
        if (!datePart) return null;
        const combined = new Date(`${datePart}T${timePart || '00:00:00'}`);
        return isNaN(combined.getTime()) ? null : combined;
    };

    // Başlangıç/bitiş tarihleri seçiliyken, "Eğitim Günü"/"Eğitim Süresi" alanlarının placeholder'ı
    // (gerçek değeri değil, yalnızca UI ipucunu) seçili aralıktan hesaplanan değeri gösterir.
    // Boş bırakılırsa backend yine katalogdaki değeri kullanır (doc §8.2) - bu yüzden bu yalnızca
    // görsel bir öneri, gerçek kaydedilen değer değil.
    const trainingDayPlaceholder = () => {
        if (formData.start && formData.end && formData.end > formData.start) {
            const s = new Date(formData.start.getFullYear(), formData.start.getMonth(), formData.start.getDate());
            const e = new Date(formData.end.getFullYear(), formData.end.getMonth(), formData.end.getDate());
            const days = Math.round((e - s) / 86400000) + 1;
            return String(days);
        }
        return '';
    };

    const trainingDurationPlaceholder = () => {
        if (formData.start && formData.end && formData.end > formData.start) {
            const hours = (formData.end.getTime() - formData.start.getTime()) / 3600000;
            return String(Math.round(hours * 100) / 100);
        }
        return '';
    };

    useEffect(() => {
        if (!visible) return;
        const preExamStart = initialData ? combineDateTime(initialData.preExamStartDate, initialData.preExamStartTime) : null;
        const preExamEnd = initialData ? combineDateTime(initialData.preExamEndDate, initialData.preExamEndTime) : null;
        setFormData({
            id: (initialData && initialData.id) ?? null,
            trainingId: (initialData && initialData.trainingId) ?? null,
            start: initialData ? combineDateTime(initialData.startDate, initialData.startTime) : null,
            end: initialData ? combineDateTime(initialData.endDate, initialData.endTime) : null,
            planningLocation: (initialData && initialData.planningLocation) ?? null,
            trainingCapacity: (initialData && initialData.trainingCapacity) ?? null,
            trainingLanguage: (initialData && initialData.trainingLanguage) ?? '',
            trainingDay: (initialData && initialData.trainingDay) ?? '',
            trainingDuration: (initialData && initialData.trainingDuration) ?? null,
            preExamRequired: preExamStart || preExamEnd ? 1 : 0,
            preExamStart,
            preExamEnd,
            fee: (initialData && initialData.fee) ?? null,
            currency: (initialData && initialData.currency) ?? '',
            isActive: initialData && initialData.isActive !== undefined && initialData.isActive !== null ? Number(initialData.isActive) : 1
        });
        setPendingInstructors([]);
        setPendingInstructorUserId(null);
        setLocalSubmitted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, initialData]);

    useEffect(() => {
        if (!visible) return;
        setNewsForm(isEdit ? toPlanningNewsForm(existingNews) : EMPTY_PLANNING_NEWS);
    }, [visible, isEdit, existingNews]);

    // Eğitim Kodu yeni seçildiğinde (yalnızca ekleme modunda) lokasyonu katalogdan öner —
    // kullanıcı sonra değiştirebilir, bu yüzden yalnızca ilk değeri set eder.
    // ÖNEMLİ AYRIM: bu efekt SADECE ekleme modunda çalışır (isEdit'te erken çıkar). trainingLanguage/
    // trainingCapacity artık `LmsCatalogPlanning`'de kalıcı olarak saklanıyor (BE güncellemesi,
    // 2026-09-01) — yani düzenleme modunda formData zaten `initialData.trainingLanguage`/
    // `initialData.trainingCapacity` (kaydedilmiş planlama değeri, reset-on-open efektinde set edildi)
    // ile açılıyor. `catalogDetail` düzenleme modunda da (görüntüleme alanları için, ör. Araç Modeli/
    // Eğitim Türü/Eğitim Adı) ayrıca çekiliyor ve bu efekt olmasa bile her zaman en güncel katalog
    // değerini taşıyor — eğer bu efekt isEdit'te de çalışsaydı, kullanıcının kaydettiği/o an
    // değiştirdiği planlamaya özel değeri, katalogdaki (eğitim kataloğu seviyesindeki, planlamadan
    // bağımsız) değerle SESSİZCE EZERDİ; kullanıcı hiç değiştirmemiş gibi görünürdü. Bu yüzden
    // planningLocation ile birlikte, YALNIZCA ekleme modunda (kullanıcı henüz hiçbir şey kaydetmemişken,
    // yalnızca ilk öneri olarak) katalogdan doldurulur.
    useEffect(() => {
        if (isEdit || !catalogDetail) return;
        setFormData((prev) => ({
            ...prev,
            planningLocation: catalogDetail.trainingLocation ?? prev.planningLocation,
            trainingCapacity: catalogDetail.trainingCapacity ?? prev.trainingCapacity,
            trainingLanguage: catalogDetail.trainingLanguage ?? prev.trainingLanguage
        }));
    }, [catalogDetail, isEdit]);

    // Eğitim Kodu değiştikçe kataloğun aktif eğitmenlerini (catalogDetail.instructors) öneri listesine kopyalar.
    useEffect(() => {
        if (isEdit || !formData.trainingId) return;
        const activeInstructors = (catalogDetail?.instructors || [])
            .filter((row) => Number(row.isActive) === 1)
            .map((row) => ({ userId: row.userId, userFullName: row.userFullName }));
        setPendingInstructors(activeInstructors);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.trainingId, catalogDetail, isEdit]);

    const handleAddPendingInstructor = () => {
        if (!pendingInstructorUserId) return;
        if (pendingInstructors.some((row) => row.userId === pendingInstructorUserId)) return;
        setPendingInstructors((prev) => [...prev, { userId: pendingInstructorUserId, userFullName: userNameById[pendingInstructorUserId] || `#${pendingInstructorUserId}` }]);
        setPendingInstructorUserId(null);
    };

    const handleRemovePendingInstructor = (userId) => {
        setPendingInstructors((prev) => prev.filter((row) => row.userId !== userId));
    };

    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        if (field === 'trainingId') return !isEdit && formData.trainingId == null;
        if (field === 'fee') return formData.fee === null || formData.fee === undefined || !(Number(formData.fee) >= 0);
        if (field === 'trainingDuration') return formData.trainingDuration != null && !(Number(formData.trainingDuration) > 0);
        if (field === 'currency') return !formData.currency || !formData.currency.trim();
        if (field === 'planningLocation') return formData.planningLocation == null;
        return false;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const preExamOn = Number(formData.preExamRequired) === 1;

        const missingRequired = (!isEdit && !formData.trainingId) || formData.planningLocation == null || !formData.currency || !formData.currency.trim() || formData.fee === null || formData.fee === undefined;
        const invalidFee = formData.fee !== null && formData.fee !== undefined && !(Number(formData.fee) >= 0);
        const invalidDuration = formData.trainingDuration != null && !(Number(formData.trainingDuration) > 0);
        const invalidMainRange = formData.start && formData.end && formData.end < formData.start;
        const invalidPreExamRange = preExamOn && formData.preExamStart && formData.preExamEnd && formData.preExamEnd < formData.preExamStart;

        if (missingRequired || invalidFee || invalidDuration) {
            toast.current?.show({ severity: 'error', summary: th('trainingCatalog.missingField'), detail: th('trainingPlanning.formValidationMessage'), life: 4000 });
            return;
        }
        if (invalidMainRange) {
            toast.current?.show({ severity: 'error', summary: th('trainingCatalog.missingField'), detail: th('trainingPlanning.dateRangeError'), life: 4000 });
            return;
        }
        if (invalidPreExamRange) {
            toast.current?.show({ severity: 'error', summary: th('trainingCatalog.missingField'), detail: th('trainingPlanning.preExamRangeError'), life: 4000 });
            return;
        }

        // trainingLanguage / trainingCapacity: BE `LmsCatalogPlanning`'e eklendi (2026-09-01),
        // bu yüzden artık payload'a gönderiliyor - bkz. dosya başındaki not.
        // "Hayır" seçiliyse ön sınav tarih/saatleri her zaman null gönderilir.
        const preExamStart = preExamOn ? formData.preExamStart : null;
        const preExamEnd = preExamOn ? formData.preExamEnd : null;

        const payload = {
            startDate: formData.start ? formatDateForApi(formData.start) : null,
            startTime: formData.start ? formatTimeForApi(formData.start) : null,
            endDate: formData.end ? formatDateForApi(formData.end) : null,
            endTime: formData.end ? formatTimeForApi(formData.end) : null,
            planningLocation: formData.planningLocation,
            trainingLanguage: formData.trainingLanguage && formData.trainingLanguage.trim() ? formData.trainingLanguage.trim() : null,
            trainingCapacity: formData.trainingCapacity != null ? Number(formData.trainingCapacity) : null,
            trainingDay: formData.trainingDay && formData.trainingDay.trim() ? formData.trainingDay.trim() : null,
            trainingDuration: formData.trainingDuration != null ? Number(formData.trainingDuration) : null,
            preExamStartDate: preExamStart ? formatDateForApi(preExamStart) : null,
            preExamStartTime: preExamStart ? formatTimeForApi(preExamStart) : null,
            preExamEndDate: preExamEnd ? formatDateForApi(preExamEnd) : null,
            preExamEndTime: preExamEnd ? formatTimeForApi(preExamEnd) : null,
            fee: Number(formData.fee),
            currency: formData.currency.trim().toUpperCase(),
            isActive: Number(formData.isActive)
        };

        const newsRequest = { ...newsForm, newsId: existingNews?.id ?? null };

        if (isEdit) {
            payload.id = formData.id;
            onSubmit(payload, undefined, newsRequest);
        } else {
            payload.trainingId = formData.trainingId;
            onSubmit(
                payload,
                pendingInstructors.map((row) => row.userId),
                newsRequest
            );
        }
    };

    const renderFooter = () => (
        <div>
            {isEdit && handleDelete && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined type="button" />
            {!readOnly && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} disabled={isEdit && isLoadingNews} />}
        </div>
    );

    return (
        <Dialog visible={visible} footer={renderFooter()} style={{ width: '50vw' }} header={isEdit ? th('trainingPlanning.editTitle') : th('trainingPlanning.addTitle')} modal className="p-fluid" onHide={onHide} key={formData.id ?? 'new'}>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit} className="p-fluid">
                <div className="grid">
                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="planningTrainingId">
                                {th('trainingPlanning.fields.trainingCode')} <span className="text-red-500">*</span>
                            </label>
                            {isEdit ? (
                                <InputText value={`${initialData?.trainingCode || ''} - ${initialData?.trainingName || ''}`} disabled />
                            ) : (
                                <Dropdown
                                    id="planningTrainingId"
                                    value={formData.trainingId}
                                    options={catalogCodeOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, trainingId: e.value }))}
                                    placeholder={th('trainingPlanning.fields.selectTrainingCodePlaceholder')}
                                    loading={isLoadingCatalogList}
                                    filter
                                    showClear
                                    appendTo={document.body}
                                    className={isFieldInvalid('trainingId') ? 'p-invalid' : ''}
                                />
                            )}
                        </div>
                    </div>

                    {formData.trainingId && (
                        <>
                            {/* Araç Modeli / Eğitim Türü: katalogdan otomatik gelir, salt okunur - bilgi
                                zaten `trainingId` ile backend'e bağlı olduğu için ayrı bir payload alanı
                                olarak GÖNDERİLMİYOR (bkz. handleSubmit). */}
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.vehicleModel')}</label>
                                    <LmsCodeDropdown groupCode="VEHICLE_MODEL" value={catalogDetail?.vehicleModel ?? null} onChange={() => {}} disabled placeholder={th('trainingPlanning.fields.selectPlaceholder')} />
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.trainingType')}</label>
                                    <LmsCodeDropdown groupCode="TRAINING_TYPES" value={catalogDetail?.trainingType ?? null} onChange={() => {}} disabled placeholder={th('trainingPlanning.fields.selectPlaceholder')} />
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.trainingName')}</label>
                                    <InputText value={catalogDetail?.trainingName || ''} disabled />
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.trainingCapacity')}</label>
                                    <InputNumber id="planningTrainingCapacity" value={formData.trainingCapacity} min={1} onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingCapacity: e.value }))} />
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.trainingLanguage')}</label>
                                    <Dropdown
                                        id="planningTrainingLanguage"
                                        value={formData.trainingLanguage || null}
                                        options={languageOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, trainingLanguage: e.value }))}
                                        placeholder={th('trainingPlanning.fields.selectPlaceholder')}
                                        loading={isLoadingLanguages}
                                        filter
                                        showClear
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.trainingContent')}</label>
                                    <InputTextarea value={contentOutputRow?.trainingContent || ''} disabled rows={3} className="w-full" />
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label>{th('trainingPlanning.fields.trainingOutcome')}</label>
                                    <InputTextarea value={contentOutputRow?.trainingOutcome || ''} disabled rows={3} className="w-full" />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="planningStart">{th('trainingPlanning.fields.startDateTime')}</label>
                            <Calendar id="planningStart" value={formData.start} onChange={(e) => setFormData((prev) => ({ ...prev, start: e.value }))} showTime hourFormat="24" dateFormat="yy-mm-dd" showIcon className="w-full" appendTo={document.body} />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="planningEnd">{th('trainingPlanning.fields.endDateTime')}</label>
                            <Calendar
                                id="planningEnd"
                                value={formData.end}
                                onChange={(e) => setFormData((prev) => ({ ...prev, end: e.value }))}
                                showTime
                                hourFormat="24"
                                dateFormat="yy-mm-dd"
                                showIcon
                                className="w-full"
                                minDate={formData.start || undefined}
                                appendTo={document.body}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="planningLocation">
                                {th('trainingPlanning.fields.planningLocation')} <span className="text-red-500">*</span>
                            </label>
                            <LmsCodeDropdown
                                id="planningLocation"
                                groupCode="TRAINING_LOCATION"
                                value={formData.planningLocation}
                                onChange={(value) => setFormData((prev) => ({ ...prev, planningLocation: value }))}
                                placeholder={th('trainingPlanning.fields.selectPlaceholder')}
                                invalid={isFieldInvalid('planningLocation')}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="planningTrainingDay">{th('trainingPlanning.fields.trainingDay')}</label>
                            <InputText id="planningTrainingDay" value={formData.trainingDay} maxLength={100} placeholder={trainingDayPlaceholder()} onChange={(e) => setFormData((prev) => ({ ...prev, trainingDay: e.target.value }))} />
                            <small className="text-color-secondary">{th('trainingPlanning.fields.trainingDayHint')}</small>
                        </div>
                    </div>

                    <div className="col-12 md:col-4">
                        <div className="field">
                            <label htmlFor="planningTrainingDuration">{th('trainingPlanning.fields.trainingDuration')}</label>
                            <InputNumber
                                id="planningTrainingDuration"
                                value={formData.trainingDuration}
                                min={1}
                                placeholder={trainingDurationPlaceholder()}
                                onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingDuration: e.value }))}
                                className={isFieldInvalid('trainingDuration') ? 'p-invalid' : ''}
                            />
                            <small className="text-color-secondary">{th('trainingPlanning.fields.trainingDurationHint')}</small>
                        </div>
                    </div>

                    <div className="col-12 md:col-4">
                        <div className="field">
                            <label htmlFor="planningFee">
                                {th('trainingPlanning.fields.fee')} <span className="text-red-500">*</span>
                            </label>
                            <InputNumber
                                id="planningFee"
                                value={formData.fee}
                                min={0}
                                mode="decimal"
                                maxFractionDigits={4}
                                onValueChange={(e) => setFormData((prev) => ({ ...prev, fee: e.value }))}
                                className={isFieldInvalid('fee') ? 'p-invalid' : ''}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-4">
                        <div className="field">
                            <label htmlFor="planningCurrency">
                                {th('trainingPlanning.fields.currency')} <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                id="planningCurrency"
                                value={formData.currency || null}
                                options={CURRENCY_OPTIONS}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => setFormData((prev) => ({ ...prev, currency: e.value }))}
                                placeholder={th('trainingPlanning.fields.selectPlaceholder')}
                                className={isFieldInvalid('currency') ? 'p-invalid' : ''}
                                appendTo={document.body}
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label>{th('trainingPlanning.fields.preExamRequired')}</label>
                            <div className="flex gap-4 mt-2">
                                <div className="flex align-items-center">
                                    <RadioButton
                                        inputId="planningPreExamRequiredYes"
                                        name="planningPreExamRequired"
                                        value={1}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, preExamRequired: e.value }))}
                                        checked={Number(formData.preExamRequired) === 1}
                                    />
                                    <label htmlFor="planningPreExamRequiredYes" className="ml-2 cursor-pointer">
                                        {th('common.yes', { fallback: 'Evet' })}
                                    </label>
                                </div>
                                <div className="flex align-items-center">
                                    <RadioButton
                                        inputId="planningPreExamRequiredNo"
                                        name="planningPreExamRequired"
                                        value={0}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, preExamRequired: e.value }))}
                                        checked={Number(formData.preExamRequired) === 0}
                                    />
                                    <label htmlFor="planningPreExamRequiredNo" className="ml-2 cursor-pointer">
                                        {th('common.no', { fallback: 'Hayır' })}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {Number(formData.preExamRequired) === 1 && (
                        <>
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="planningPreExamStart">{th('trainingPlanning.fields.preExamStart')}</label>
                                    <Calendar id="planningPreExamStart" value={formData.preExamStart} onChange={(e) => setFormData((prev) => ({ ...prev, preExamStart: e.value }))} showTime hourFormat="24" dateFormat="yy-mm-dd" showIcon className="w-full" appendTo={document.body} />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="planningPreExamEnd">{th('trainingPlanning.fields.preExamEnd')}</label>
                                    <Calendar
                                        id="planningPreExamEnd"
                                        value={formData.preExamEnd}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, preExamEnd: e.value }))}
                                        showTime
                                        hourFormat="24"
                                        dateFormat="yy-mm-dd"
                                        showIcon
                                        className="w-full"
                                        minDate={formData.preExamStart || undefined}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="col-12 md:col-4">
                        <div className="field">
                            <label>{th('common.status')}</label>
                            <div className="flex gap-4 mt-2">
                                <div className="flex align-items-center">
                                    <RadioButton inputId="planningIsActiveYes" name="planningIsActive" value={1} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))} checked={Number(formData.isActive) === 1} />
                                    <label htmlFor="planningIsActiveYes" className="ml-2 cursor-pointer">
                                        {th('common.active', { fallback: 'Aktif' })}
                                    </label>
                                </div>
                                <div className="flex align-items-center">
                                    <RadioButton inputId="planningIsActiveNo" name="planningIsActive" value={0} onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))} checked={Number(formData.isActive) === 0} />
                                    <label htmlFor="planningIsActiveNo" className="ml-2 cursor-pointer">
                                        {th('common.inactive', { fallback: 'Pasif' })}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <PlanningNewsSection value={newsForm} onChange={setNewsForm} existingNews={existingNews} loading={isEdit && isLoadingNews} disabled={!!loading} />

            {isEdit ? (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--surface-border)' }}>
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-users" />
                        <span className="font-semibold">{th('trainingPlanning.instructors.dialogTitle')}</span>
                    </div>
                    <PlanningInstructorsTab planningId={formData.id} readOnly={instructorsReadOnly} />
                </div>
            ) : (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--surface-border)' }}>
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-users" />
                        <span className="font-semibold">{th('trainingPlanning.instructors.dialogTitle')}</span>
                        {pendingInstructors.length > 0 && <span className="px-2 py-1 border-round text-xs font-semibold bg-blue-100 text-blue-800">{pendingInstructors.length}</span>}
                    </div>
                    {formData.trainingId ? (
                        <>
                            <div className="flex align-items-end gap-2 mb-3">
                                <div style={{ minWidth: '20rem' }}>
                                    <label className="block mb-1">{th('trainingPlanning.instructors.selectUser')}</label>
                                    <InstructorUserDropdown
                                        value={pendingInstructorUserId}
                                        onChange={setPendingInstructorUserId}
                                        excludeUserIds={pendingInstructors.map((row) => row.userId)}
                                        placeholder={th('trainingPlanning.instructors.selectUserPlaceholder')}
                                        className="w-full"
                                    />
                                </div>
                                <Button type="button" label={th('trainingPlanning.instructors.addButton')} icon="pi pi-plus" onClick={handleAddPendingInstructor} disabled={!pendingInstructorUserId} />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {pendingInstructors.map((row) => (
                                    <Chip key={row.userId} label={row.userFullName || `#${row.userId}`} removable onRemove={() => handleRemovePendingInstructor(row.userId)} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-color-secondary">
                            <i className="pi pi-info-circle mr-2" />
                            {th('trainingPlanning.instructors.selectTrainingCodeFirstHint')}
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
};

export default PlanningForm;
